<template>
  <GbwapsLayout v-if="article" :title="article.h1" :intro="pageIntro" :breadcrumb="breadcrumb">
    <article class="blog-article-page">
      <p class="blog-article-meta">
        <span class="article-category">{{ article.category }}</span>
        <span class="blog-article-meta-line">
          By GBWaps · {{ article.dateLabel || article.year }} · {{ article.readTime }}
        </span>
      </p>

      <figure v-if="article.coverImage" class="blog-article-cover">
        <img
          :src="article.coverImage"
          :alt="article.coverImageAlt || article.h1"
          loading="eager"
          width="800"
          height="420">
      </figure>

      <section
        v-for="section in article.sections"
        :id="section.id"
        :key="section.id"
        class="page-section site-section">
        <h2 v-if="section.h2">{{ section.h2 }}</h2>

        <ContentBlocks v-if="section.blocks && section.blocks.length" :blocks="section.blocks" />

        <div v-if="section.steps && section.steps.length" class="blog-howto-steps">
          <div v-for="(step, idx) in section.steps" :key="idx" class="blog-howto-step">
            <h3>Step {{ idx + 1 }}: {{ step.title }}</h3>
            <ContentBlocks v-if="step.blocks && step.blocks.length" :blocks="step.blocks" />
            <ContentBlocks v-else-if="step.parts" :blocks="[{ parts: step.parts }]" />
            <p v-else-if="step.text">{{ step.text }}</p>
            <ul v-if="step.list && step.list.length" class="blog-bullet-list">
              <li v-for="(item, li) in step.list" :key="li">{{ item }}</li>
            </ul>
            <ContentBlocks
              v-if="step.blocksAfter && step.blocksAfter.length"
              :blocks="step.blocksAfter" />
          </div>
        </div>

        <ul v-if="section.list && section.list.length" class="blog-bullet-list">
          <li v-for="(item, idx) in section.list" :key="idx">
            <template v-if="typeof item === 'string'">{{ item }}</template>
            <template v-else>
              <strong v-if="item.strong">{{ item.strong }}</strong>{{ item.text }}
            </template>
          </li>
        </ul>

        <div v-if="section.table" class="table-scroll">
          <table class="comparison-table">
            <thead>
              <tr>
                <th v-for="header in section.table.headers" :key="header">{{ header }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in section.table.rows" :key="row.feature">
                <td>{{ row.feature }}</td>
                <td>{{ row.official }}</td>
                <td>{{ row.gb }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <ContentBlocks
          v-if="section.blocksAfter && section.blocksAfter.length"
          :blocks="section.blocksAfter" />

        <div v-if="section.subsections && section.subsections.length" class="blog-subsections">
          <div v-for="(sub, idx) in section.subsections" :key="idx" class="blog-subsection">
            <h3>{{ sub.h3 }}</h3>
            <ContentBlocks :blocks="sub.blocks" />
          </div>
        </div>

        <div v-if="section.faqs && section.faqs.length" class="blog-faq-list">
          <div v-for="(faq, idx) in section.faqs" :key="idx" class="blog-faq-item">
            <h3>{{ faq.q }}</h3>
            <ContentBlocks v-if="faq.aParts" :blocks="[{ parts: faq.aParts }]" />
            <p v-else>{{ faq.a }}</p>
          </div>
        </div>
      </section>

      <div class="blog-article-cta card">
        <h3>Continue on GBWaps</h3>
        <p>
          <router-link :to="article.relatedLink.to">{{ article.relatedLink.text }}</router-link>
          ·
          <router-link to="/blog/">Back to blog</router-link>
        </p>
      </div>
    </article>
  </GbwapsLayout>
  <GbwapsLayout
    v-else
    title="Article not found"
    intro="This blog post does not exist."
    :breadcrumb="[{ name: 'Home', to: '/' }, { name: 'Blog', to: '/blog/' }]">
    <p>
      <router-link to="/blog/">Return to GB WhatsApp Blog</router-link>
    </p>
  </GbwapsLayout>
</template>

<script>
import '@/css/gbwaps/pages.css';
import GbwapsLayout from '@/layouts/GbwapsLayout.vue';
import ContentBlocks from '@/components/gbwaps/ContentBlocks.vue';
import { BLOG_POSTS_BY_SLUG } from '@/data/blog-content';

export default {
	name: 'BlogArticlePage',
	components: { GbwapsLayout, ContentBlocks },
	props: {
		slug: {
			type: String,
			required: true,
		},
	},
	computed: {
		article() {
			return BLOG_POSTS_BY_SLUG[this.slug] || null;
		},
		pageIntro() {
			if (!this.article || this.article.hidePageIntro) return '';
			return this.article.excerpt || '';
		},
		breadcrumb() {
			if (!this.article) {
				return [{ name: 'Home', to: '/' }, { name: 'Blog', to: '/blog/' }];
			}
			return [
				{ name: 'Home', to: '/' },
				{ name: 'Blog', to: '/blog/' },
				{ name: this.article.title },
			];
		},
	},
};
</script>
